/**
 * chc-admin-web 의 report-pdf.vue 를 그대로 이식한 클라이언트 PDF 생성 컴포넌트.
 * - reportPDFApi.getReportPDFData() 로 admin 과 동일한 데이터({ patnt, parnt })를 받아
 *   화면 밖(off-screen)에 리포트 DOM 을 렌더 → chart.js 로 차트를 그린 뒤
 *   html2canvas + jsPDF 로 PDF 를 직접 생성한다.
 * - admin 은 doc.save() 로 브라우저 다운로드했지만, 여기선 웹뷰이므로
 *   base64 로 뽑아 window.flutter_inappwebview.callHandler('downloadPDF', base64Data) 로 넘긴다.
 *
 * 기존 report-part-body.view.tsx 는 건드리지 않고 이 파일에만 이식했다.
 */
import React, { forwardRef, useEffect, useImperativeHandle, useRef, useState } from 'react';
import Chart from 'chart.js/auto';
import ChartDataLabels from 'chartjs-plugin-datalabels';
import annotationPlugin from 'chartjs-plugin-annotation';
import html2canvas from 'html2canvas';
import { jsPDF } from 'jspdf';
import base64Font from '../../../../assets/font/fontBase64';
import { youngAdultLegendInfoByCode } from './report-pdf.const';
import { IReportPdfPerson, IReportPdfSurveyInfoItem, ReportAPI } from '../../../../apis/v1/report.api';
import useAuthentication from '../../../../store/useAuthentication';
import BarInfoView from '../../common/bar-info-view';
import idreamPlaystore from '../../../../assets/images/report/idream_playstore.png';

Chart.register(annotationPlugin);

// 차트 내부 폰트 크기. 그래프 폭이 좁아(535px) 폰트가 상대적으로 커지므로 축소.
const CHART_LABEL_FONT = { size: 9 }; // 구분선(우수/보통/심각) 라벨
const CHART_DATALABEL_SIZE = 11; // 값 라벨(꼭짓점 숫자)
const CHART_TICK_FONT = { size: 10 }; // 축 눈금

export interface ReportPdfHandle {
  downRealPdf: () => void;
  downTestPdf: (years: number) => void;
}

interface ReportPartBodyParseViewProps {
  onLoaded?: (loaded: boolean) => void;
  showButton?: boolean; // 자체 "결과 다운로드" 버튼 노출 여부 (기본 true)
  onPdfSuccess?: () => void; // PDF 생성/전송 성공
  onPdfError?: () => void; // PDF 생성 실패
}

const ReportPartBodyParseView = forwardRef<ReportPdfHandle, ReportPartBodyParseViewProps>(({ onLoaded, showButton = true, onPdfSuccess, onPdfError }, ref) => {
  const { authentication } = useAuthentication();
  const reportPDFApi = new ReportAPI(process.env.REACT_APP_ENV !== 'dev', authentication.accessToken, authentication.deviceId);

  // ── 렌더용 상태 (admin 의 refs) ──
  const [parntReportResult, setParntReportResult] = useState<any>(null);
  const [patntReportResult, setPatntReportResult] = useState<any>(null);
  const [parntGood, setParntGood] = useState<IReportPdfSurveyInfoItem[]>([]);
  const [parntNormal, setParntNormal] = useState<IReportPdfSurveyInfoItem[]>([]);
  const [parntDanger, setParntDanger] = useState<IReportPdfSurveyInfoItem[]>([]);
  const [patntGood, setPatntGood] = useState<IReportPdfSurveyInfoItem[]>([]);
  const [patntNormal, setPatntNormal] = useState<IReportPdfSurveyInfoItem[]>([]);
  const [patntDanger, setPatntDanger] = useState<IReportPdfSurveyInfoItem[]>([]);
  const [patntNm, setPatntNm] = useState<string>('');
  const [parntNm, setParntNm] = useState<string>('');
  const [parntAge, setParntAge] = useState<number>(0);
  const [patntAge, setPatntAge] = useState<number>(0);
  const [parntGender, setParntGender] = useState<string>('');
  const [patntGender, setPatntGender] = useState<string>('');
  const [surveyTime, setSurveyTime] = useState<number | undefined>(undefined);

  // ── 함수 간 공유용 mutable 값 (admin 의 ref().value) ──
  const patntDescData = useRef<string>('');
  const patntDescHeight = useRef<number>(0);
  const parntDescData = useRef<string>('');
  const parntDescHeight = useRef<number>(0);
  const totalPage = useRef<number>(0);

  // API 로 받은 원본 dataList (테스트 데이터 생성 시 구조 베이스로 사용)
  const rawDataList = useRef<any>(null);

  function removeParenthesis(str: string) {
    const index = str.indexOf(' (');
    return index !== -1 ? str.substring(0, index) : str;
  }

  function getRefName(v: number | string) {
    return 'chart-' + v;
  }

  async function convertChartsToImages() {
    const chartElements = document.querySelectorAll('canvas.pdfs');
    for (const canvas of Array.from(chartElements)) {
      const chartInstance = Chart.getChart((canvas as HTMLCanvasElement).id);
      if (chartInstance) {
        const imgSrc = chartInstance.toBase64Image();
        const img = new Image();
        img.src = imgSrc;
        // 캔버스가 있던 고정 박스(535×285)를 그대로 채운다. 변환 성공/실패와 무관하게
        // 그래프 슬롯 높이가 일정하게 유지되도록 원본 캔버스와 동일한 크기로 맞춘다.
        img.style.width = '100%';
        img.style.height = '285px';
        img.style.objectFit = 'contain';
        img.style.border = 'none';
        const parent = canvas.parentNode as HTMLElement;
        parent.insertBefore(img, canvas);
        parent.removeChild(canvas);
      }
    }
  }

  async function captureElement(element: Element) {
    const wrapper = document.createElement('div');
    wrapper.appendChild(element.cloneNode(true));
    (document.querySelector('#service-sheet') as HTMLElement).appendChild(wrapper);
    const canvas = await html2canvas(wrapper, { logging: false, scale: 2.5 });
    wrapper.remove();
    return canvas;
  }

  async function captureElements(elements: NodeListOf<Element>) {
    const promises = Array.from(elements).map((element) => captureElement(element));
    return await Promise.all(promises);
  }

  async function downPdf() {
    try {
      await convertChartsToImages();

      const imgWidth = 200;
      const pageHeight = 297;
      let patCount = 0;
      let parCount = 0;

      const header = document.querySelector('#pdf-header');
      const footer = document.querySelector('#pdf-footer');

      const headerCanvas = await html2canvas(header as HTMLElement, { logging: false });
      const footerCanvas = await html2canvas(footer as HTMLElement, { logging: false });

      const headerImgData = headerCanvas.toDataURL('image/jpeg', 1);
      const footerImgData = footerCanvas.toDataURL('image/jpeg', 1);

      const headerHeight = (headerCanvas.height * imgWidth) / headerCanvas.width;
      const footerHeight = (footerCanvas.height * imgWidth) / footerCanvas.width;

      let patntArray: any;
      let patntMainData: any;
      let patntMainHeight: any;
      let parntArray: any;
      let parntMainData: any;
      let parntMainHeight: any;

      if (patntReportResult) {
        const patntHeader = document.querySelector('#patnt-header');
        const patntHeaderCanvas = await html2canvas(patntHeader as HTMLElement, { logging: false });
        const patntElements = document.querySelectorAll('.patnt-graph');
        patntMainData = patntHeaderCanvas.toDataURL('image/jpeg', 1);
        patntMainHeight = (patntHeaderCanvas.height * imgWidth) / patntHeaderCanvas.width;
        patntArray = await captureElements(patntElements);
        patCount = Math.ceil(patntElements.length / 3 + 1);
      }
      const patntDescHeader = document.querySelector('#patnt-desc');
      const patntDescCanvas = await html2canvas(patntDescHeader as HTMLElement, { logging: false });
      patntDescData.current = patntDescCanvas.toDataURL('image/jpeg', 1);
      patntDescHeight.current = (patntDescCanvas.height * imgWidth) / patntDescCanvas.width;

      const parntDescHeader = document.querySelector('#parnt-desc');
      const parntDescCanvas = await html2canvas(parntDescHeader as HTMLElement, { logging: false });
      parntDescData.current = parntDescCanvas.toDataURL('image/jpeg', 1);
      parntDescHeight.current = (parntDescCanvas.height * imgWidth) / parntDescCanvas.width;

      if (parntReportResult) {
        const parntHeader = document.querySelector('#parnt-header');
        const parntHeaderCanvas = await html2canvas(parntHeader as HTMLElement, { logging: false });
        const parntElements = document.querySelectorAll('.parnt-graph');
        parntMainData = parntHeaderCanvas.toDataURL('image/jpeg', 1);
        parntMainHeight = (parntHeaderCanvas.height * imgWidth) / parntHeaderCanvas.width;
        parntArray = await captureElements(parntElements);
        parCount = Math.ceil(parntElements.length / 3 + 1);
      }

      totalPage.current = patCount + parCount;
      const doc = new jsPDF('portrait', 'mm', 'a4');
      doc.addFileToVFS('NotoSansKR.ttf', base64Font);
      doc.addFont('NotoSansKR.ttf', 'NotoSansKR', 'normal');
      doc.setFont('NotoSansKR');
      doc.setFontSize(10);

      if (patntReportResult) {
        processCanvasArray(patntArray, doc, patntMainData, patntMainHeight, headerImgData, headerHeight, footerImgData, footerHeight, pageHeight, imgWidth, 'patnt');
      }
      if (parntReportResult) {
        processCanvasArray(parntArray, doc, parntMainData, parntMainHeight, headerImgData, headerHeight, footerImgData, footerHeight, pageHeight, imgWidth, 'parnt');
      }

      // ── 운영(웹뷰): base64 로 뽑아 플러터로 넘긴다 ──
      const base64Data = doc.output('datauristring').split(',')[1];
      await window.flutter_inappwebview.callHandler('downloadPDF', base64Data);

      // ── 로컬 테스트용: 브라우저에서 바로 다운로드 ──
      // let title = '소아암_희귀질환_패널_결과_리포트_';
      // if (parntNm) title += parntNm + '_';
      // if (patntNm) title += patntNm + '_';
      // title += '.pdf';
      // doc.save(title);

      onPdfSuccess?.();
    } catch (e) {
      console.error(e);
      onPdfError?.();
    }
  }

  // 페이지네이션: mainHeight 임계값 분기(기기마다 다른 결과 + 그래프 1개/페이지 버그)를 버리고,
  // "다음 그래프가 페이지 하단을 넘으면 새 페이지" 방식으로 누적 배치한다. 기기 무관하게 페이지를 꽉 채운다.
  function processCanvasArray(
    canvasArray: any,
    doc: any,
    mainData: any,
    mainHeight: any,
    headerImgData: any,
    headerHeight: any,
    footerImgData: any,
    footerHeight: any,
    pageHeight: any,
    imgWidth: any,
    type: any
  ) {
    const descData = type === 'patnt' ? patntDescData.current : parntDescData.current;
    const descHeight = type === 'patnt' ? patntDescHeight.current : parntDescHeight.current;
    const bottomLimit = pageHeight - footerHeight - 5; // 푸터 위 최소 여백

    // 1) 요약(우수/보통/심각) 페이지. 사람(환아/보호자)마다 새 페이지에서 시작.
    if (mainData) {
      if (doc.__used) doc.addPage();
      addHeaderAndFooter(doc, headerImgData, headerHeight, footerImgData, footerHeight, pageHeight, imgWidth, type, 'main');
      doc.addImage(mainData, 'JPEG', 5, headerHeight, imgWidth, mainHeight);
      doc.__used = true;
    }

    // 2) 그래프 페이지들. 첫 그래프 페이지 상단에만 "누적 설문 결과"(desc) 를 얹고 그래프를 채운다.
    let positions = 0;
    let firstGraphPage = true;
    const newGraphPage = () => {
      doc.addPage();
      doc.__used = true;
      addHeaderAndFooter(doc, headerImgData, headerHeight, footerImgData, footerHeight, pageHeight, imgWidth, type, 'content');
      if (firstGraphPage) {
        doc.addImage(descData, 'JPEG', 5, headerHeight, imgWidth, descHeight);
        positions = headerHeight + descHeight + 2;
        firstGraphPage = false;
      } else {
        positions = headerHeight + 2;
      }
    };
    newGraphPage();

    canvasArray.forEach((canvas: any) => {
      if (!(canvas instanceof HTMLCanvasElement)) {
        console.error('유효하지 않은 canvas:', canvas);
        return;
      }
      const imgData = canvas.toDataURL('image/jpeg', 1);
      const imgHeight = (canvas.height * imgWidth) / canvas.width;

      // 이번 그래프가 페이지 하단을 넘기면 새 페이지로
      if (positions + imgHeight > bottomLimit) {
        newGraphPage();
      }
      doc.addImage(imgData, 'JPEG', 5, positions, imgWidth, imgHeight);
      positions += imgHeight;
    });
  }

  function addHeaderAndFooter(doc: any, headerImgData: any, headerHeight: any, footerImgData: any, footerHeight: any, pageHeight: any, imgWidth: any, type: any, type2: any) {
    if (headerImgData) {
      doc.addImage(headerImgData, 'JPEG', 5, 0, imgWidth, headerHeight);
    }
    if (footerImgData) {
      doc.addImage(footerImgData, 'JPEG', 5, pageHeight - footerHeight, imgWidth, footerHeight);
    }
    const pageNumberText = `Page ${doc.getNumberOfPages()}`;
    doc.text(pageNumberText, imgWidth - 6, pageHeight - 5, { align: 'center' });
    doc.text(type === 'patnt' ? '환아 리포트' : '보호자 리포트', imgWidth - 50, pageHeight - 5, { align: 'center' });
    doc.setFontSize(10);
  }

  async function pdf() {
    const dataList = await reportPDFApi.getReportPDFData();
    if (dataList) {
      rawDataList.current = dataList;
      applyReportData(dataList);
    }
  }

  // dataList(API 또는 테스트 생성분)를 화면 상태에 반영한다. 차트는 상태 변경 후 useEffect 에서 그린다.
  function applyReportData(dataList: any) {
    if (dataList.parnt) {
      const parnt: IReportPdfPerson = dataList.parnt;
      setParntReportResult(parnt.survey);
      const parntReportInfo = parnt.surveyInfo;
      setParntGood(parntReportInfo.filter((item) => item.colorCd === 'GREEN'));
      setParntNormal(parntReportInfo.filter((item) => item.colorCd === 'ORANGE'));
      setParntDanger(parntReportInfo.filter((item) => item.colorCd === 'RED'));
      setParntNm(parnt.name);
      setParntAge(parnt.age);
      setParntGender(parnt.gender === 'F' ? '여' : '남');
      setSurveyTime(parnt.surveyTime);
    } else {
      setParntReportResult(null);
    }
    if (dataList.patnt) {
      const patnt: IReportPdfPerson = dataList.patnt;
      setPatntReportResult(patnt.survey);
      const patntReportInfo = patnt.surveyInfo;
      setPatntGood(patntReportInfo.filter((item) => item.colorCd === 'GREEN'));
      setPatntNormal(patntReportInfo.filter((item) => item.colorCd === 'ORANGE'));
      setPatntDanger(patntReportInfo.filter((item) => item.colorCd === 'RED'));
      setPatntNm(patnt.name);
      setPatntAge(patnt.age);
      setPatntGender(patnt.gender === 'F' ? '여' : '남');
    } else {
      setPatntReportResult(null);
    }
    onLoaded?.(true);
  }

  // 상태(차트 데이터) 변경 후 차트를 그린다. (admin 의 nextTick 대응)
  useEffect(() => {
    if (parntReportResult) {
      for (const parnt of parntReportResult) {
        for (const item of parnt.data) {
          chartDraw(item);
        }
      }
    }
    if (patntReportResult) {
      for (const patnt of patntReportResult) {
        for (const item of patnt.data) {
          chartDraw(item);
        }
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [parntReportResult, patntReportResult]);

  // API 로 받은 원본 데이터 구조를 기반으로 n(1~9)년도 임의 데이터를 생성
  function generateTestData(years: number) {
    if (!rawDataList.current) return null;
    const dataList = JSON.parse(JSON.stringify(rawDataList.current));
    for (const who of ['patnt', 'parnt']) {
      const person = dataList[who];
      if (!person) continue;
      person.surveyTime = years;
      for (const section of person.survey) {
        for (const item of section.data) {
          const base = item.graph && item.graph[0] ? item.graph[0] : { color: 'GREEN', itemName: '' };
          const isDecimal = String(base.value).includes('.');
          // 그래프별 y축 최소값을 하한으로 (예: CHI_M_13 은 15 미만 값이 나오지 않도록)
          const floor = youngAdultLegendInfoByCode[item.graphCd]?.yAxis?.[0] || 0;
          const graph = [];
          for (let y = 1; y <= years; y++) {
            let v = floor + Math.random() * (item.maxValue - floor);
            v = isDecimal ? Math.round(v * 10) / 10 : Math.round(v);
            if (v >= item.maxValue) v = item.maxValue - (isDecimal ? 0.1 : 1);
            if (v < floor) v = floor;
            graph.push({ nthYear: y, color: base.color, value: v, itemName: base.itemName });
          }
          item.graph = graph;
        }
      }
    }
    return dataList;
  }

  // 테스트 PDF: n년도 임의 데이터를 생성해 렌더 후 다운로드
  // 일반 PDF: API 로 받은 실제 데이터로 다시 렌더한 뒤 다운로드
  function downRealPdf() {
    // 다운로드 시점에 사용할 데이터가 없으면 실패 모달을 띄운다.
    if (!rawDataList.current) {
      onPdfError?.();
      return;
    }
    applyReportData(JSON.parse(JSON.stringify(rawDataList.current)));
    setTimeout(() => downPdf(), 100);
  }

  function downTestPdf(years: number) {
    const testData = generateTestData(years);
    if (!testData) {
      onPdfError?.();
      return;
    }
    applyReportData(testData);
    // 차트 렌더(animation:false 로 즉시 완성) 후 캡처
    setTimeout(() => downPdf(), 100);
  }

  useImperativeHandle(ref, () => ({ downRealPdf, downTestPdf }), [parntReportResult, patntReportResult]);

  useEffect(() => {
    pdf();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  function findValueByNthYear(objects: any[], n: number) {
    const found = objects.find((obj) => obj.nthYear === n);
    return found ? found.value : null;
  }

  // legendList 의 각 value 위치에 등급 색/라벨의 점선을 그린다.
  // CHI_B_11, CHI_M_13 은 점선 위/아래 보조 라벨이 필요한 특이케이스
  function buildLegendAnnotations(legendList: any[], code: string) {
    const annotations: any = {};
    legendList.forEach((legend, i) => {
      if (legend.value == null) return;
      annotations['line' + i] = {
        type: 'line',
        yMin: legend.value,
        yMax: legend.value,
        borderColor: legend.color,
        borderWidth: 2,
        borderDash: [10, 5],
        label: {
          display: true,
          content: legend.value + ', ' + legend.label,
          position: 'end',
          color: legend.color,
          xAdjust: 67,
          textAlign: 'start',
          backgroundColor: 'white',
          font: CHART_LABEL_FONT,
        },
      };
    });

    // 특이케이스 1) CHI_B_11: 검은 점선 1개 — 위쪽 "600 이상, 우수", 아래쪽 "600 미만, 심각"
    if (code === 'CHI_B_11' && annotations.line0) {
      const v = legendList[0].value;
      annotations.line0.label.content = v + ' 이상, 우수';
      annotations.line0.label.color = '#40A640';
      annotations.line0.label.yAdjust = -12;
      annotations.line0Below = {
        type: 'line',
        yMin: v,
        yMax: v,
        borderColor: 'rgba(0,0,0,0)',
        borderWidth: 0,
        label: {
          display: true,
          content: v + ' 미만, 심각',
          position: 'end',
          color: '#F24D4D',
          xAdjust: 67,
          yAdjust: 12,
          textAlign: 'start',
          backgroundColor: 'white',
          font: CHART_LABEL_FONT,
        },
      };
    }

    // 특이케이스 2) CHI_M_13: low(첫 등급) "18.5, 우수" 라벨은 위·왼쪽으로,
    // 점선 아래쪽엔 orange "18.5 미만, 보통" 추가
    if (code === 'CHI_M_13' && legendList.length) {
      const low = legendList[0].value;
      if (annotations.line0) {
        annotations.line0.label.position = 'end';
        annotations.line0.label.xAdjust = 67;
        annotations.line0.label.yAdjust = -16;
      }
      annotations.belowLow = {
        type: 'line',
        yMin: low,
        yMax: low,
        borderColor: 'rgba(0,0,0,0)',
        borderWidth: 0,
        label: {
          display: true,
          content: low + ' 미만, 보통',
          position: 'end',
          color: '#FFB800',
          xAdjust: 67,
          yAdjust: 16,
          textAlign: 'start',
          backgroundColor: 'white',
          font: CHART_LABEL_FONT,
        },
      };
    }

    return annotations;
  }

  function chartDraw(chartData: any) {
    let ctx: any;
    let datasets: any[] = [];
    const refName = getRefName(chartData.graphId);
    const color = ['rgba(0, 0, 255, 1)', 'rgba(255, 105, 180, 1)'];

    // ── YA(세분화) 케이스: graphCd 가 코드맵에 있으면 세부 등급 점선/커스텀 y축 적용 ──
    const legendInfo = youngAdultLegendInfoByCode[chartData.graphCd];
    const legendList = legendInfo?.legendList;
    const yAxis = legendInfo?.yAxis;
    const maxValue = legendInfo?.maxValue;
    // maxValue 초과 값은 축 상단으로 캡, null(미응답)은 선의 공백으로 유지
    const capValue = (v: any) => (v == null ? null : maxValue ? Math.min(v, maxValue) : v);

    if (!checkGraphItemName(chartData.graph)) {
      const arr = [];
      let graphItemNames = chartData.graph.filter((item: any) => item.itemName).map((item: any) => item.itemName);
      graphItemNames = Array.from(new Set(graphItemNames));
      let index = 0;
      for (const name of graphItemNames) {
        const itemGraph = chartData.graph.filter((item: any) => item.itemName === name);
        arr.push(itemGraph);
        const dataArr = [
          findValueByNthYear(itemGraph, 1),
          findValueByNthYear(itemGraph, 2),
          findValueByNthYear(itemGraph, 3),
          findValueByNthYear(itemGraph, 4),
          findValueByNthYear(itemGraph, 5),
          findValueByNthYear(itemGraph, 6),
          findValueByNthYear(itemGraph, 7),
          findValueByNthYear(itemGraph, 8),
          findValueByNthYear(itemGraph, 9),
        ];
        datasets.push({
          label: name,
          data: dataArr.map(capValue),
          originalData: dataArr,
          borderColor: color[index],
          backgroundColor: color[index],
        });
        index++;
      }
    } else {
      const dataArr = [
        findValueByNthYear(chartData.graph, 1),
        findValueByNthYear(chartData.graph, 2),
        findValueByNthYear(chartData.graph, 3),
        findValueByNthYear(chartData.graph, 4),
        findValueByNthYear(chartData.graph, 5),
        findValueByNthYear(chartData.graph, 6),
        findValueByNthYear(chartData.graph, 7),
        findValueByNthYear(chartData.graph, 8),
        findValueByNthYear(chartData.graph, 9),
      ];
      datasets = [
        {
          label: chartData.itemName,
          data: dataArr.map(capValue),
          originalData: dataArr,
          borderColor: color[0],
          backgroundColor: color[0],
        },
      ];
    }
    // 재렌더(테스트 반복) 시 같은 캔버스의 기존 차트 제거
    Chart.getChart(refName)?.destroy();
    const el = document.getElementById(refName) as HTMLCanvasElement | null;
    if (!el) return;
    ctx = el.getContext('2d');
    const options: any = {
      type: 'line',
      data: {
        labels: ['1차년도', '2차년도', '3차년도', '4차년도', '5차년도', '6차년도', '7차년도', '8차년도', '9차년도'],
        datasets: datasets,
      },
      options: {
        animation: false,
        // 그래프 슬롯 높이를 고정(래퍼 250px)하고 캔버스가 그 높이를 채우게 한다.
        // 비율(aspectRatio)에 맡기면 웹뷰 환경/변환 실패 시 캔버스가 세로로 길게 잡혀
        // 페이지당 그래프 수가 들쭉날쭉해진다. 고정 높이면 환경 무관하게 항상 동일.
        responsive: true,
        maintainAspectRatio: false,
        layout: {
          padding: {
            top: 44,
            right: 60,
          },
        },
        scales: {
          y: {
            ticks: { font: CHART_TICK_FONT },
            ...(yAxis ? { min: yAxis[0] } : { suggestedMin: 0 }),
            ...(maxValue ? { max: maxValue } : { suggestedMax: chartData.maxValue * 1.2 }),
            ...(yAxis
              ? {
                  afterBuildTicks: (axis: any) => {
                    axis.ticks = yAxis.map((v: number) => ({ value: v }));
                  },
                }
              : {}),
          },
          x: {
            ticks: { font: CHART_TICK_FONT },
          },
        },
        plugins: {
          legend: {
            display: !datasets[0].label ? false : true,
          },
          tooltip: {
            yAlign: 'bottom',
          },
          datalabels: {
            anchor: 'end',
            align: function (context: any) {
              // 선이 2개(예: 우울/불안)면 매 지점마다 값이 큰 쪽 라벨은 위, 작은 쪽은 아래로
              // 배치해 라벨이 항상 바깥으로 벌어지도록(가운데서 겹치지 않도록) 한다
              const datasets = context.chart.data.datasets;
              if (datasets.length > 1) {
                const i = context.dataIndex;
                const my = context.dataset.data[i];
                const other = datasets[context.datasetIndex === 0 ? 1 : 0].data[i];
                if (my == null || other == null) return 'top';
                if (my === other) return context.datasetIndex === 0 ? 'top' : 'bottom';
                return my > other ? 'top' : 'bottom';
              }
              return 'top';
            },
            font: {
              weight: 'bold',
              size: CHART_DATALABEL_SIZE,
            },
            color: function (context: any) {
              return color[context.datasetIndex];
            },
            // 값이 급변해 선이 급경사로 지나가도 라벨이 선/축 위에 떠서 읽히도록 배경 박스를 깐다
            backgroundColor: 'rgba(255, 255, 255, 0.7)',
            borderRadius: 10,
            padding: { top: 2, bottom: 2, left: 4, right: 4 },
            clamp: true,
            formatter: function (value: any, context: any) {
              return context.dataset.originalData ? context.dataset.originalData[context.dataIndex] : value;
            },
          },
          annotation: legendList
            ? { annotations: buildLegendAnnotations(legendList, chartData.graphCd) }
            : {
                annotations: {
                  line1: {
                    type: 'line',
                    yMin: chartData.colorLineInfo.greenMaxLine,
                    yMax: chartData.colorLineInfo.greenMaxLine,
                    borderColor: 'rgba(0, 255, 0, 1)',
                    borderWidth: 2,
                    borderDash: [10, 5],
                    label: {
                      display: true,
                      content: chartData.colorLineInfo.greenMaxLine + ', 우수',
                      position: 'end',
                      color: 'rgba(0, 255, 0, 1)',
                      xAdjust: 67,
                      textAlign: 'start',
                      backgroundColor: 'white',
                      font: CHART_LABEL_FONT,
                    },
                  },
                  line2: {
                    type: 'line',
                    yMin: chartData.colorLineInfo.orangeMaxLine,
                    yMax: chartData.colorLineInfo.orangeMaxLine,
                    borderColor: 'rgba(255, 165, 0, 1)',
                    borderWidth: 2,
                    borderDash: [10, 5],
                    label: {
                      display: true,
                      content: chartData.colorLineInfo.orangeMaxLine + ', 보통',
                      position: 'end',
                      color: 'rgba(255, 165, 0, 1)',
                      xAdjust: 67,
                      backgroundColor: 'white',
                      font: CHART_LABEL_FONT,
                    },
                  },
                  line3: {
                    type: 'line',
                    yMin: chartData.colorLineInfo.redMaxLine,
                    yMax: chartData.colorLineInfo.redMaxLine,
                    borderColor: 'rgba(255, 0, 0, 1)',
                    borderWidth: 2,
                    borderDash: [10, 5],
                    label: {
                      display: true,
                      content: chartData.colorLineInfo.redMaxLine + ', 심각',
                      position: 'end',
                      color: 'rgba(255, 0, 0, 1)',
                      xAdjust: 67,
                      backgroundColor: 'white',
                      font: CHART_LABEL_FONT,
                    },
                  },
                },
              },
        },
        clip: false,
      },
      plugins: [ChartDataLabels],
    };
    // eslint-disable-next-line no-new
    new Chart(ctx, options);
  }

  function checkGraphItemName(array: any[]) {
    const emptyItem = array.filter((item) => item.itemName === '');
    return emptyItem.length > 0;
  }

  return (
    <>
      {showButton && <BarInfoView buildDomToImage={downRealPdf} />}
      {/* PDF 캡처 소스 DOM. 화면 밖에 렌더해 실제 화면 레이아웃을 건드리지 않는다. */}
      <div style={{ position: 'absolute', top: -99999, left: -99999, background: 'white' }} aria-hidden>
        <div id="pdf-form" className="custom-font" style={{ background: 'white', width: '210mm', fontFamily: 'Pretendard-Medium, sans-serif' }}>
          <div id="pdf-header" style={{ position: 'relative', borderBottom: '1px solid', height: '66px' }}>
            <img src={idreamPlaystore} style={{ width: '60px', height: '60px', border: 'none' }} alt="" />
            <p style={{ position: 'absolute', left: '70px', top: '14px', fontSize: '32px', lineHeight: 1.15, margin: 0 }}>아이드림</p>
            <p style={{ position: 'absolute', right: 0, top: '8px', textAlign: 'right', fontWeight: 'bold', lineHeight: 1.35, margin: 0 }}>
              소아암 희귀질환 패널 결과 리포트 – {surveyTime}차<br />
              {patntReportResult && (
                <span>
                  환아명: {patntNm}({patntAge}세, {patntGender}),
                </span>
              )}
              <span>
                보호자명: {parntNm}({parntAge}세, {parntGender})
              </span>
            </p>
          </div>

          {patntReportResult && (
            <div id="patnt-header" className="section">
              <p className="mt-4" style={{ fontWeight: 'bold', fontSize: '16px', marginBottom: '20px' }}>
                {surveyTime}차년도 환아 종합 설문결과
              </p>
              <div style={{ border: '1px solid #999', display: 'flex' }}>
                <div style={{ flex: 1, display: 'flex', borderRight: '1px solid #999', flexDirection: 'column' }}>
                  <div style={{ textAlign: 'center', padding: '20px', borderBottom: '1px solid #999', backgroundColor: '#2fed28' }}>우수</div>
                  <div style={{ minHeight: '120px' }}>
                    {patntGood.map((v, i) => (
                      <p key={i} style={{ fontSize: '12px', padding: '5px' }}>
                        {v.mnmtToolNm} - {removeParenthesis(v.graphNm)}
                        {v.graphItemNm ? '(' + v.graphItemNm + ')' : ''}
                      </p>
                    ))}
                  </div>
                </div>
                <div style={{ flex: 1, display: 'flex', borderRight: '1px solid #999', flexDirection: 'column' }}>
                  <div style={{ textAlign: 'center', padding: '20px', borderBottom: '1px solid #999', backgroundColor: '#ffa500' }}>보통</div>
                  <div>
                    {patntNormal.map((v, i) => (
                      <p key={i} style={{ fontSize: '12px', padding: '5px' }}>
                        {v.mnmtToolNm} - {removeParenthesis(v.graphNm)}
                        {v.graphItemNm ? '(' + v.graphItemNm + ')' : ''}
                      </p>
                    ))}
                  </div>
                </div>
                <div style={{ flex: 1, display: 'flex', flexDirection: 'column' }}>
                  <div style={{ textAlign: 'center', padding: '20px', borderBottom: '1px solid #999', backgroundColor: '#ff5a5a' }}>심각</div>
                  <div>
                    {patntDanger.map((v, i) => (
                      <p key={i} style={{ fontSize: '12px', padding: '5px' }}>
                        {v.mnmtToolNm} - {removeParenthesis(v.graphNm)}
                        {v.graphItemNm ? '(' + v.graphItemNm + ')' : ''}
                      </p>
                    ))}
                  </div>
                </div>
              </div>
              <p style={{ marginTop: '10px', fontSize: '12px' }}>*최신 차수에 대한 종합 결과입니다. 응답값에 따라 결과가 계산되어 영역별로 표시됩니다.</p>
            </div>
          )}

          {patntReportResult &&
            patntReportResult.map((patnt: any, index: number) => (
              <div key={index} className="py-4" style={{ fontWeight: 'bold' }}>
                {patnt.data.map((item: any, i: number) => (
                  <div key={i} className="pb-3 section patnt-graph" style={{ paddingBottom: '6px' }}>
                    <p style={{ margin: 0, lineHeight: 1.5 }}>
                      <span style={{ fontWeight: 'bold', fontSize: '14px' }}>
                        {index + 1}.{patnt.name}
                        {youngAdultLegendInfoByCode[item.graphCd] ? ' (성인)' : ''} - {item.graphNm}
                      </span>
                    </p>
                    <div style={{ width: '535px', height: '285px', margin: '4px auto' }}>
                      <canvas className="pdfs" id={getRefName(item.graphId)} />
                    </div>
                  </div>
                ))}
              </div>
            ))}

          {parntReportResult && (
            <div id="parnt-header" className="section">
              <p className="mt-4" style={{ fontWeight: 'bold', fontSize: '16px', marginBottom: '20px' }}>
                {surveyTime}차년도 보호자 종합 설문결과
              </p>
              <div style={{ border: '1px solid #999', display: 'flex' }}>
                <div style={{ flex: 1, display: 'flex', borderRight: '1px solid #999', flexDirection: 'column' }}>
                  <div style={{ textAlign: 'center', padding: '20px', borderBottom: '1px solid #999', backgroundColor: '#2fed28' }}>우수</div>
                  <div style={{ minHeight: '120px' }}>
                    {parntGood.map((v, i) => (
                      <p key={i} style={{ fontSize: '12px', padding: '5px' }}>
                        {v.mnmtToolNm} - {removeParenthesis(v.graphNm)}
                        {v.graphItemNm ? '(' + v.graphItemNm + ')' : ''}
                        {v.userType === 'C' ? ' (환아)' : ''}
                      </p>
                    ))}
                  </div>
                </div>
                <div style={{ flex: 1, display: 'flex', borderRight: '1px solid #999', flexDirection: 'column' }}>
                  <div style={{ textAlign: 'center', padding: '20px', borderBottom: '1px solid #999', backgroundColor: '#ffa500' }}>보통</div>
                  <div>
                    {parntNormal.map((v, i) => (
                      <p key={i} style={{ fontSize: '12px', padding: '5px' }}>
                        {v.mnmtToolNm} - {removeParenthesis(v.graphNm)}
                        {v.graphItemNm ? '(' + v.graphItemNm + ')' : ''}
                        {v.userType === 'C' ? ' (환아)' : ''}
                      </p>
                    ))}
                  </div>
                </div>
                <div style={{ flex: 1, display: 'flex', flexDirection: 'column' }}>
                  <div style={{ textAlign: 'center', padding: '20px', borderBottom: '1px solid #999', backgroundColor: '#ff5a5a' }}>심각</div>
                  <div>
                    {parntDanger.map((v, i) => (
                      <p key={i} style={{ fontSize: '12px', padding: '5px' }}>
                        {v.mnmtToolNm} - {removeParenthesis(v.graphNm)}
                        {v.graphItemNm ? '(' + v.graphItemNm + ')' : ''}
                        {v.userType === 'C' ? ' (환아)' : ''}
                      </p>
                    ))}
                  </div>
                </div>
              </div>
              <p style={{ marginTop: '10px', fontSize: '12px' }}>*최신 차수에 대한 종합 결과입니다. 응답값에 따라 결과가 계산되어 영역별로 표시됩니다.</p>
            </div>
          )}

          {parntReportResult &&
            parntReportResult.map((parnt: any, index: number) => (
              <div key={index} className="py-4" style={{ fontWeight: 'bold' }}>
                {parnt.data.map((item: any, i: number) => (
                  <div key={i} className="pb-3 section parnt-graph" style={{ paddingBottom: '6px' }}>
                    <p style={{ margin: 0, lineHeight: 1.5 }}>
                      <span style={{ fontWeight: 'bold', fontSize: '14px' }}>
                        {index + 1}.{parnt.name}
                        {youngAdultLegendInfoByCode[item.graphCd] ? ' (성인)' : ''} - {item.graphNm} {item.userType === 'C' ? ' (환아)' : ''}
                      </span>
                    </p>
                    <div style={{ width: '535px', height: '285px', margin: '4px auto' }}>
                      <canvas className="pdfs" id={getRefName(item.graphId)} />
                    </div>
                  </div>
                ))}
              </div>
            ))}

          <div id="pdf-footer" style={{ borderTop: '1px solid', paddingTop: '20px' }}>
            <p style={{ height: '8mm' }} />
          </div>
        </div>

        <div id="patnt-desc" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', background: 'white', padding: '4px 2px' }}>
          <p style={{ fontWeight: 'bold', fontSize: '16px', lineHeight: 1.4, margin: 0 }}>누적 설문 결과</p>
          <p style={{ textAlign: 'right', fontSize: '9px', lineHeight: 1.4, margin: 0 }}>*환아가 응답한 시점 부터 결과가 노출되며, 환아는 8세 이후 직접 설문 참여 가능합니다.</p>
        </div>
        <div id="parnt-desc" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', background: 'white', padding: '4px 2px' }}>
          <p style={{ fontWeight: 'bold', fontSize: '16px', lineHeight: 1.4, margin: 0 }}>누적 설문 결과</p>
        </div>
        <p id="service-sheet" style={{ width: '210mm', position: 'absolute', top: '-9999px', left: '-9999px' }} />
      </div>
    </>
  );
});

ReportPartBodyParseView.displayName = 'ReportPartBodyParseView';

export default ReportPartBodyParseView;
